import {inject, Injectable} from '@angular/core';
import {
  FileInfo,
  OptionInfo,
  OptionVersion,
  ProjectSubsection,
  ShareLinkAsset,
  ShareLinkAssetEntityTypes,
  TopicInfo,
} from '../../shared/interfaces';
import {TreeSortMode} from '../../shared/models';
import {ShareLinkAssetNode, ShareLinkItemNode, ShareLinkNodeData, TreeNodeType,} from '../models/tree-node';
import {ProjectService} from '../../shared/services/project.service';
import {BaseTreeService} from './base-tree.service';
import {
  getShareLinkAssetFromVM,
  prepareShareLinkAsset,
  prepareShareLinkAssetsData,
} from '../utilities/share-link-utilities';
import {getVersionId} from '../utilities/subsection-utilities';
import {SharePreviewTreeParams} from "../models";

@Injectable({
  providedIn: 'root',
})
export class SharePreviewTreeService extends BaseTreeService<ShareLinkNodeData> {
  private readonly projectService = inject(ProjectService);
  private options: OptionInfo[] = [];
  private topics: TopicInfo[] = [];

  buildTree(params: SharePreviewTreeParams) {
    const { shareLink, options, topics, sortMode, subsection } = params;
    this.options = options;
    this.topics = topics;
    prepareShareLinkAssetsData(shareLink);
    const rootNode: ShareLinkItemNode = {
      id: shareLink.id,
      name: shareLink.title,
      type: TreeNodeType.ShareLinkItem,
      lastUpdated: shareLink.updatedAt,
      children: [],
      parent: null,
      isExpanded: true,
      isSelected: true,
      isExpandable: true,
      depth: 0,
      data: shareLink,
    };

    this.state = {
      root: rootNode,
      paths: {
        [shareLink.id]: [rootNode.id],
      },
      selectedNode: rootNode,
      expandedNodes: [],
    };

    shareLink.tree.forEach((asset, index) => {
      this.buildAssetNode({
        asset,
        parent: rootNode,
        topics,
        options,
        sortMode,
        subsection,
      });
    });

    this.sortTree(sortMode, false);
    this.updateState(this.state);
  }

  buildAssetNode(params: {
    asset: ShareLinkAsset;
    parent: ShareLinkItemNode | ShareLinkAssetNode;
    topics?: TopicInfo[];
    options?: OptionInfo[];
    subsection?: ProjectSubsection;
    sortMode?: TreeSortMode;
  }): ShareLinkAssetNode {
    const { asset, parent, topics, options, sortMode, subsection } = params;

    if (asset.entityType == ShareLinkAssetEntityTypes.SUBFOLDER) {
      const option = options?.find((o) => o.optionId == asset.optionId);
      if (option?.simpleVersions?.length === 1) {
        asset.singleVersionNumber = option.simpleVersions[0].versionNumber;
        asset.versionId = getVersionId(asset.optionId, asset.singleVersionNumber);
      }
    } else if (asset.entityType == ShareLinkAssetEntityTypes.VERSION) {
      asset.versionId = asset.itemId;
    }

    prepareShareLinkAsset(asset);

    const assetNode: ShareLinkAssetNode = {
      id: asset.itemId,
      name: asset.name,
      type: TreeNodeType.ShareLinkAsset,
      isExpandable: false,
      isSelected: false,
      isExpanded: false,
      children: [],
      parent: parent,
      depth: parent.depth + 1,
      assetType: asset.entityType,
      data: asset,
    };

    if (asset.entityType == ShareLinkAssetEntityTypes.VERSION) {
      assetNode.name = `Version ${asset.version}`;
      asset.name = assetNode.name;
    }

    this.addNodeToParent(parent, assetNode);

    if (asset.children) {
      if (
        asset.entityType == ShareLinkAssetEntityTypes.SUBFOLDER &&
        asset.children.length == 1 &&
        asset.children[0].children.length > 0
      ) {
        asset.singleVersionNumber = asset.children[0].version;

        asset.children[0].children.forEach((child) => {
          this.buildAssetNode({
            asset: child,
            parent: assetNode,
            topics,
            options,
            subsection,
            sortMode,
          });
        });
      } else {
        if (asset.entityType == ShareLinkAssetEntityTypes.VERSION) {
          asset.versionId = asset.itemId;
        }

        asset.children.forEach((child) => {
          this.buildAssetNode({
            asset: child,
            parent: assetNode,
            topics,
            options,
            subsection,
            sortMode,
          });
        });
      }
    }
    return assetNode;
  }

  sortTree(sortMode: TreeSortMode, updateState?: boolean) {
    if (!this.state.root) return;
    this.sortNode(this.state.root, sortMode);
    this.updateState({
      root: this.state.root,
    });
  }

  private sortNode(node: ShareLinkNodeData, sortMode: TreeSortMode) {
    if (!node) return;
    this.sortChildren(node, sortMode);

    node.children.forEach((child) => {
      if (child.children && child.children.length > 0) {
        this.sortNode(child as ShareLinkNodeData, sortMode);
      }
    });
  }

  private sortChildren(node: ShareLinkNodeData, sortMode: TreeSortMode) {
    if (!node.children) {
      return;
    }

    // Only skip sorting if all children are version nodes
    const allVersionNodes = (node.children as ShareLinkAssetNode[]).every(
      (child) => child.assetType == ShareLinkAssetEntityTypes.VERSION
    );

    if (allVersionNodes) {
      return;
    }

    node.children.sort((a, b) => {
      const assetA = a as ShareLinkAssetNode;
      const assetB = b as ShareLinkAssetNode;

      switch (sortMode) {
        case TreeSortMode.lastUpdated:
          return (assetB.data.updatedAt || 0) - (assetA.data.updatedAt || 0);
        case TreeSortMode.firstUpload:
          return (assetA.data.firstUploaded || 0) - (assetB.data.firstUploaded || 0);
        case TreeSortMode.lastUpload:
          return (assetB.data.lastUploaded || 0) - (assetA.data.lastUploaded || 0);
        default:
          return (assetA.name || '').localeCompare(assetB.name || '', undefined, { sensitivity: 'base' });
      }
    });
  }

  populateVersionFilesById(params: {
    parentId: string;
    option: OptionInfo;
    subsection: ProjectSubsection;
    version: OptionVersion;
    sortMode: TreeSortMode;
  }) {
    const parent = this.findNodeById(params.parentId) as ShareLinkAssetNode;
    if (!parent) return;

    this.populateVersionFiles({
      parent,
      option: params.option,
      subsection: params.subsection,
      version: params.version,
      sortMode: params.sortMode,
    });
  }

  populateVersionFiles(params: {
    parent: ShareLinkAssetNode;
    option: OptionInfo;
    subsection: ProjectSubsection;
    version: OptionVersion;
    sortMode: TreeSortMode;
  }) {
    const { parent, version, option, subsection, sortMode } = params;
    const versionSimple = option.simpleVersions.find((v) => v.versionNumber == version.versionNumber);

    for (let i = parent.children.length - 1; i >= 0; i--) {
      this.deleteNode(parent.children[i] as ShareLinkAssetNode);
    }

    version.files.forEach((file: FileInfo) => {
      const asset = getShareLinkAssetFromVM({
        file,
        option,
        version: versionSimple,
        type: ShareLinkAssetEntityTypes.FILE,
        subsection,
      });
      const assetNode: ShareLinkAssetNode = {
        id: asset.itemId,
        name: asset.name,
        type: TreeNodeType.ShareLinkAsset,
        isExpandable: false,
        isSelected: false,
        isExpanded: false,
        children: [],
        parent: parent,
        depth: parent.depth + 1,
        assetType: asset.entityType,
        data: asset,
      };
      this.addNodeToParent(parent, assetNode);
    });
  }
}
